import { formatKg } from '@cadencia/domain';
import type { SetRecommendation } from '../../data/services/recommendationService.js';

/**
 * Traduccion de los motivos ESTRUCTURADOS a lenguaje llano.
 * El «¿Por qué?» se compone de `reason_codes` + el snapshot de inputs, no de
 * texto libre: asi la explicacion existe aunque la IA no este disponible
 * (docs/AI_ARCHITECTURE.md §6).
 */
const MOTIVOS: Record<string, string> = {
  first_time: 'Es la primera vez que registras este ejercicio en esta máquina.',
  noisy_history: 'Tu historial en este ejercicio es irregular, así que voy con prudencia.',
  double_progression_load_up: 'La última vez completaste el tope del rango con el RIR previsto.',
  double_progression_add_rep: 'Mantengo la carga y busco una repetición más.',
  load_progression_up: 'La última vez te sobró margen respecto al RIR objetivo.',
  load_progression_hold: 'Mantengo la carga hasta que el RIR objetivo se cumpla con holgura.',
  rep_progression_jump: 'Toca saltar de carga: el rango se completó.',
  rep_progression_add_rep: 'El salto de carga disponible es grande, así que subo repeticiones.',
  top_backoff: 'Serie de aproximación al top set.',
  rir_ramp: 'El RIR objetivo baja según avanza el bloque.',
  readiness_high: 'Tu readiness de hoy es alto.',
  readiness_normal: 'Tu readiness de hoy está en rango normal.',
  readiness_reduced: 'Tu readiness de hoy está algo bajo: reduzco un 5 %.',
  readiness_low: 'Tu readiness de hoy es bajo: reduzco un 10 % y subo el RIR objetivo.',
  readiness_very_low: 'Tu readiness de hoy es muy bajo: reduzco un 15 %.',
  prefer_stable_variants: 'Con este readiness conviene evitar ejercicios de alta demanda técnica.',
  consider_moving_session: 'Plantéate mover la sesión o convertirla en trabajo técnico.',
  no_checkin: 'Hoy no hay check-in de readiness, así que no modulo por ese lado.',
  intra_session_fatigue: 'Llevas bastantes series efectivas hoy: descuento algo de fatiga.',
  deload_no_increase: 'Estás en semana de descarga: no subo carga aunque el rendimiento lo permita.',
  return_after_pause:
    'Hace semanas que no haces este ejercicio: empiezo algo por debajo de la última vez y ajustamos con lo que salga hoy.',
  region_soreness: 'Tienes agujetas marcadas en la zona que trabaja este ejercicio.',
  override_bias: 'He aprendido que en este ejercicio sueles usar algo más de carga y te funciona.',
  override_bias_down:
    'He aprendido que en este ejercicio sueles bajar la carga que te propongo y el resultado sale mejor.',
  clamped_by_safety_limit: 'He recortado el salto para no superar el 10 % entre sesiones.',
  rounded_within_safety_bounds: 'He ajustado el redondeo para no rebasar el límite de seguridad.',
  coarse_increment: 'El incremento disponible es grande: compenso con una repetición.',
  equipment_not_configured: 'Este equipamiento no tiene cargas configuradas todavía.',
  within_dead_band: 'La serie anterior salió dentro de lo previsto: no cambio la carga.',
  slightly_below_target: 'La serie anterior quedó algo por debajo del objetivo.',
  below_target: 'La serie anterior quedó claramente por debajo del objetivo.',
  well_below_target: 'La serie anterior quedó muy por debajo del objetivo.',
  slightly_above_target: 'La serie anterior quedó algo por encima del objetivo.',
  above_target: 'La serie anterior quedó claramente por encima del objetivo.',
  no_increase_on_last_set: 'Es la última serie: no subo carga, lo anoto para la próxima sesión.',
  increase_blocked_low_readiness: 'No subo carga porque tu readiness de hoy es bajo.',
  increase_blocked_soreness: 'No subo carga por las agujetas de esa zona.',
  decrease_reinforced: 'Refuerzo la bajada por readiness bajo o agujetas.',
  clamped_intra_session_cumulative:
    'He limitado el ajuste para no alejarme más del 10 % de la primera serie.',
};

export function describeReasonCode(code: string): string {
  return MOTIVOS[code] ?? code;
}

export interface Explicacion {
  datos: string[];
  queCambio: string;
  objetivo: string;
  resultadoEsperado: string;
  reevaluacion: string;
}

export function explainRecommendation(
  recomendacion: SetRecommendation,
  contexto: { ejercicio: string; serie: number; totalSeries: number },
): Explicacion {
  const snapshot = recomendacion.inputsSnapshot;
  const datos: string[] = [];

  const e1rm = snapshot['e1rmKg'];
  if (typeof e1rm === 'number' && e1rm > 0) {
    const sesiones = snapshot['e1rmSessions'];
    datos.push(
      `Máximo estimado: ${formatKg(e1rm)}${
        typeof sesiones === 'number' ? ` (media de ${sesiones} sesiones)` : ''
      }`,
    );
  }

  const ultima = snapshot['lastSet'];
  if (ultima && typeof ultima === 'object') {
    const set = ultima as { loadKg: number; reps: number; rir: number };
    datos.push(`Serie anterior: ${formatKg(set.loadKg)} × ${set.reps} @ RIR ${set.rir}`);
  }

  const readiness = snapshot['readinessToday'];
  if (typeof readiness === 'number') datos.push(`Readiness de hoy: ${readiness}/100`);

  const agujetas = snapshot['regionSoreness'];
  if (typeof agujetas === 'number') datos.push(`Agujetas en la zona: ${agujetas}/5`);

  const seriesPrevias = snapshot['sessionSetsBefore'];
  if (typeof seriesPrevias === 'number' && seriesPrevias > 0) {
    datos.push(`Series efectivas ya hechas hoy: ${seriesPrevias}`);
  }

  const queCambio =
    recomendacion.loadKg === null
      ? 'No propongo carga todavía.'
      : `Carga recomendada: ${formatKg(recomendacion.loadKg)} para ${recomendacion.targetReps} repeticiones a RIR ${recomendacion.targetRir}.`;

  return {
    datos: datos.length > 0 ? datos : ['Todavía no hay datos suficientes de este ejercicio.'],
    queCambio,
    objetivo: recomendacion.reasonCodes.map(describeReasonCode).join(' '),
    resultadoEsperado:
      recomendacion.loadKg === null
        ? 'Con la carga que elijas hoy tendré referencia para la próxima sesión.'
        : `Terminar la serie ${contexto.serie} de ${contexto.totalSeries} de ${contexto.ejercicio} cerca de RIR ${recomendacion.targetRir}.`,
    reevaluacion: 'Se recalcula en la siguiente serie y en la próxima sesión de este ejercicio.',
  };
}
